import { useState } from "react";
import { MapPin, Phone, Mail, Clock, Navigation } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { COMPANY, PHONES, MAP_EMBED, MAP_LINK } from "@/data/content";
import { SectionLabel } from "@/components/SectionLabel";
import { AccentHeading } from "@/components/AccentHeading";
import { Reveal } from "@/components/Reveal";
import { PillButton } from "@/components/PillButton";
import { Dropdown } from "@/components/Dropdown";
import { openWhatsApp, buildEnquiryMessage } from "@/lib/whatsapp";

const INTERESTS = ["2 BHK Flat", "3 BHK Luxury Flat", "Row House", "Other"];

const InfoCard = ({ icon: Icon, label, children, href, green }) => {
  const inner = (
    <div className="glass-card flex items-start gap-4 rounded-2xl p-4 transition-colors hover:border-[var(--border-hover)]">
      <span className={`grid h-10 w-10 shrink-0 place-items-center rounded-xl border border-[var(--border-subtle)] ${green ? "text-[#25D366]" : "text-[var(--accent-secondary)]"} bg-[var(--bg-accent-surface)]`}>
        <Icon size={18} />
      </span>
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-[0.18em] text-[var(--text-muted)]">{label}</div>
        <div className="mt-1 text-sm text-[var(--text-secondary)] break-words">{children}</div>
      </div>
    </div>
  );
  return href ? (
    <a href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" className="block focus-ring rounded-2xl">{inner}</a>
  ) : inner;
};

export const Contact = () => {
  const [form, setForm] = useState({ name: "", phone: "", email: "", interest: "", message: "" });
  const [errors, setErrors] = useState({});

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.name.trim()) errs.name = "Please enter your name";
    if (!form.phone.trim()) errs.phone = "Please enter your phone number";
    else if (!/^[0-9+\-\s()]{7,}$/.test(form.phone)) errs.phone = "Enter a valid phone number";
    setErrors(errs);
    if (Object.keys(errs).length) return;
    openWhatsApp(buildEnquiryMessage(form));
  };

  const inputCls =
    "w-full rounded-xl border border-[var(--border-subtle)] bg-[rgba(255,245,230,0.03)] px-4 py-3 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] outline-none transition-colors focus:border-[var(--border-hover)] focus-ring";

  return (
    <section id="contact" data-testid="contact" className="bg-[var(--bg-warm)] py-16 md:py-24 lg:py-28">
      <div className="container-pad">
        <div className="max-w-2xl">
          <Reveal><SectionLabel>GET IN TOUCH</SectionLabel></Reveal>
          <Reveal delay={0.05}>
            <AccentHeading as="h2" text="Let&rsquo;s Build Your Dream *Home*" className="mt-4 block text-4xl leading-[1.05] md:text-5xl" />
          </Reveal>
        </div>

        <div className="mt-12 grid gap-8 lg:grid-cols-2 lg:gap-12">
          <div className="flex flex-col gap-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <InfoCard icon={MapPin} label="Office Address" href={MAP_LINK}>{COMPANY.address}</InfoCard>
              <div className="flex flex-col gap-4">
                <InfoCard icon={Phone} label={PHONES.founder.name} href={`tel:${PHONES.founder.tel}`}>{PHONES.founder.phone}</InfoCard>
                <InfoCard icon={FaWhatsapp} label="WhatsApp" green href="https://wa.me/917447555058">+91 7447 555 058</InfoCard>
              </div>
            </div>
            <InfoCard icon={Mail} label="Email" href={`mailto:${COMPANY.email}`}>{COMPANY.email}</InfoCard>
            <InfoCard icon={Clock} label="Office Hours">{COMPANY.hours}</InfoCard>

            <div data-testid="contact-map" className="mt-1 overflow-hidden rounded-[24px] border border-[var(--border-subtle)] shadow-[var(--shadow-sm)]">
              <iframe
                title="Godavari Builders office location on Google Maps"
                src={MAP_EMBED}
                width="100%"
                height="260"
                style={{ border: 0, filter: "invert(0.9) hue-rotate(180deg) brightness(0.95) contrast(0.9)" }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          <Reveal delay={0.1}>
            <form data-testid="contact-form" onSubmit={submit} className="glass-card rounded-[28px] p-6 md:p-8">
              <h3 className="font-playfair text-2xl text-[var(--text-primary)]">Request a Callback</h3>
              <p className="mt-2 text-sm text-[var(--text-secondary)]">Fill the form &mdash; we&rsquo;ll continue the conversation instantly on WhatsApp.</p>

              <div className="mt-6 flex flex-col gap-4">
                <div>
                  <label htmlFor="c-name" className="mb-1.5 block text-xs uppercase tracking-[0.16em] text-[var(--text-muted)]">Full Name *</label>
                  <input id="c-name" data-testid="contact-name-input" value={form.name} onChange={update("name")} placeholder="Your name" className={inputCls} />
                  {errors.name && <span className="mt-1 block text-xs text-[#e08a6a]">{errors.name}</span>}
                </div>
                <div>
                  <label htmlFor="c-phone" className="mb-1.5 block text-xs uppercase tracking-[0.16em] text-[var(--text-muted)]">Phone *</label>
                  <input id="c-phone" data-testid="contact-phone-input" value={form.phone} onChange={update("phone")} placeholder="+91 …" inputMode="tel" className={inputCls} />
                  {errors.phone && <span className="mt-1 block text-xs text-[#e08a6a]">{errors.phone}</span>}
                </div>
                <div>
                  <label htmlFor="c-email" className="mb-1.5 block text-xs uppercase tracking-[0.16em] text-[var(--text-muted)]">Email</label>
                  <input id="c-email" data-testid="contact-email-input" value={form.email} onChange={update("email")} placeholder="you@example.com" inputMode="email" className={inputCls} />
                </div>
                <div>
                  <span className="mb-1.5 block text-xs uppercase tracking-[0.16em] text-[var(--text-muted)]">Interested In</span>
                  <Dropdown
                    value={form.interest}
                    onChange={(v) => setForm((f) => ({ ...f, interest: v }))}
                    options={INTERESTS}
                    testId="contact-interest-select"
                  />
                </div>
                <div>
                  <label htmlFor="c-message" className="mb-1.5 block text-xs uppercase tracking-[0.16em] text-[var(--text-muted)]">Message</label>
                  <textarea id="c-message" data-testid="contact-message-textarea" value={form.message} onChange={update("message")} rows={4} placeholder="Tell us what you&rsquo;re looking for…" className={`${inputCls} resize-none`} />
                </div>
                <PillButton type="submit" data-testid="contact-submit-whatsapp-button" icon={FaWhatsapp} className="mt-1 w-full">
                  Send on WhatsApp
                </PillButton>
              </div>
            </form>
          </Reveal>
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          <PillButton variant="outline" href={`tel:${PHONES.founder.tel}`} icon={Phone} showIcon>Call Now</PillButton>
          <PillButton variant="outline" href={`mailto:${COMPANY.email}`} icon={Mail} showIcon>Email Us</PillButton>
          <PillButton variant="outline" href={MAP_LINK} icon={Navigation} showIcon>Get Directions</PillButton>
        </div>
      </div>
    </section>
  );
};
